/** Section 3.7 - the controller contract, and the vocabulary the planner speaks.
 *
 * `PlayerController` belongs in the kernel's public surface eventually; it is
 * declared here because `packages/core` is owned by another agent and adding a
 * shared type to it is not a drive-by edit. The shape is copied verbatim from
 * the constitution, so moving it later is a re-export, not a rewrite.
 */

import type { BattleCommand, GameCommand, PlayerObservation } from "@game/core";

export interface PlayerController {
  readonly kind: "human" | "ai" | "replay" | "remote";
  chooseCommand(obs: PlayerObservation, signal: AbortSignal): Promise<GameCommand>;
}

/**
 * One scored option. `why` is not decoration: every number the AI acts on has to
 * be explainable, or a balance report is unreadable and a regression is
 * untraceable. It is a short tag, never a sentence to show a player.
 */
export interface Candidate {
  readonly command: GameCommand;
  readonly score: number;
  readonly why: string;
}

/** Deterministic ordering: score descending, then the tag, then the shape. */
export function compareCandidates(a: Candidate, b: Candidate): number {
  if (a.score !== b.score) return b.score - a.score;
  if (a.why !== b.why) return a.why < b.why ? -1 : 1;
  const ka = commandKey(a.command), kb = commandKey(b.command);
  return ka === kb ? 0 : ka < kb ? -1 : 1;
}

/**
 * A stable string for one command. Used to sort ties and to stop the AI issuing
 * the same no-progress command twice in a turn; it never enters state.
 */
export function commandKey(c: GameCommand): string {
  if (c.type === "MOVE_HERO") return `MOVE_HERO:${c.hero}:${c.path.join(",")}`;
  if (c.type === "BATTLE") return `BATTLE:${battleCommandKey(c.cmd)}`;
  return fieldKey(c.type, c as unknown as Record<string, unknown>);
}

export function battleCommandKey(c: BattleCommand): string {
  return fieldKey(c.type, c as unknown as Record<string, unknown>);
}

function fieldKey(type: string, o: Record<string, unknown>): string {
  const parts: string[] = [type];
  for (const k of Object.keys(o).sort()) {
    if (k === "type") continue;
    parts.push(`${k}=${String(o[k])}`);
  }
  return parts.join(":");
}
